<script>
  /**
   * The one Mature Content (18+) opt-in, shared by Studio Settings, Render Setup
   * and the wizard entry. It writes the persisted preference directly through
   * setMatureContentPreference (no Save step), gates the first enable behind the
   * age confirmation, and shows the server's plan note when a filter-off render
   * would be refused. Hosts that keep a draft copy of the setting sync it in
   * `onChange`. Renders nothing while signed out.
   *
   * Visual contract: one accent only. The switch track turns pink when on;
   * everything else (title, tag, copy) stays in the host's text colours so the
   * control reads as a preference, not a warning. The full form is a field row
   * like its neighbours; the blur option is a smaller switch indented under it.
   */
  import { auth } from '../state/auth.svelte.js';
  import {
    studioSettings,
    viewingPreferences,
    setMatureContentPreference,
    setBlurMatureContentPreference
  } from '../state/settings.svelte.js';
  import { matureFilterPlanNote } from '../utils/mature-content.js';

  let { compact = false, onChange = null } = $props();

  // Inline 18+ confirmation: shown the first time the switch is turned on.
  let confirmPending = $state(false);
  // The compact pill's popover closes on Escape and on a tap outside it.
  let compactRoot = $state(null);

  const enabled = $derived(studioSettings.matureContentEnabled === true);
  const blurOn = $derived(viewingPreferences.blurMatureContent !== false);
  const planNote = $derived(matureFilterPlanNote({
    unlocked: auth.sensitiveContentFilterUnlocked,
    isUnlimited: auth.isUnlimited,
    subscription: auth.subscription,
    premiumSpark: auth.balance?.premiumSpark
  }));

  const CONFIRM_TEXT = 'These tools can produce adult (NSFW) content. Confirm you are 18 or older to turn this on.';

  function dismissOnEscape(event) {
    if (compact && confirmPending && event.key === 'Escape') confirmPending = false;
  }

  function dismissOutside(event) {
    if (compact && confirmPending && compactRoot && !compactRoot.contains(event.target)) confirmPending = false;
  }

  function commit(next) {
    setMatureContentPreference(next);
    confirmPending = false;
    onChange?.(studioSettings.matureContentEnabled === true);
  }

  function toggle(event) {
    if (!auth.isLoggedIn) return;
    // A second press while the confirmation is open collapses it.
    if (confirmPending) {
      confirmPending = false;
      if (event?.currentTarget && 'checked' in event.currentTarget) event.currentTarget.checked = false;
      return;
    }
    if (enabled) {
      commit(false);
      return;
    }
    if (studioSettings.matureContentConfirmed) {
      commit(true);
      return;
    }
    // Ask first. The preference stays off, so revert the native checkbox the
    // click just toggled (Svelte leaves a controlled input alone when the bound
    // value did not change).
    confirmPending = true;
    if (event?.currentTarget && 'checked' in event.currentTarget) event.currentTarget.checked = false;
  }

  function confirm(event) {
    // The confirm buttons unmount on click; stop the event so a host's
    // click-outside handler never sees a detached target and closes itself.
    event?.stopPropagation();
    commit(true);
  }

  function cancel(event) {
    event?.stopPropagation();
    confirmPending = false;
  }

  function toggleBlur(event) {
    setBlurMatureContentPreference(event.currentTarget.checked);
  }
</script>

{#snippet switchRow({ checked, onchange, title, tag = null, sub, small = false })}
  <label class="mature-row" class:small>
    <input type="checkbox" class="mature-input" {checked} {onchange} />
    <span class="mature-track" aria-hidden="true"><span class="mature-thumb"></span></span>
    <span class="mature-copy">
      <span class="mature-title">{title}{#if tag} <span class="mature-tag">{tag}</span>{/if}</span>
      <span class="mature-sub">{sub}</span>
    </span>
  </label>
{/snippet}

{#snippet confirmation()}
  <div class="mature-confirm" role="group" aria-label="Confirm mature content">
    <p class="mature-confirm-text">{CONFIRM_TEXT}</p>
    {#if planNote}<p class="mature-note">{planNote}</p>{/if}
    <div class="mature-confirm-actions">
      <button type="button" class="btn-secondary mature-btn" onclick={cancel}>Not now</button>
      <button type="button" class="btn-primary mature-btn" onclick={confirm}>I'm 18 or older</button>
    </div>
  </div>
{/snippet}

<svelte:window onkeydown={dismissOnEscape} onpointerdown={dismissOutside} />

{#if auth.isLoggedIn}
  {#if compact}
    <div class="mature-compact" bind:this={compactRoot}>
      <button
        type="button"
        class="mature-pill"
        class:active={enabled}
        aria-pressed={enabled}
        aria-expanded={confirmPending}
        title={enabled ? 'Mature themes are on for your account' : 'Allow mature themes (18+)'}
        onclick={toggle}
      >
        Mature themes <span class="mature-tag">18+</span>
      </button>
      {#if confirmPending}
        <div class="mature-popover">{@render confirmation()}</div>
      {/if}
    </div>
  {:else}
    <div class="mature" class:active={enabled}>
      {@render switchRow({ checked: enabled, onchange: toggle, title: 'Mature Content', tag: '18+',
        sub: 'Allows adult themes in your prompts and renders, and unlocks the spicier models.' })}

      {#if planNote && !confirmPending}
        <p class="mature-note">{planNote}</p>
      {/if}

      {#if confirmPending}
        {@render confirmation()}
      {/if}

      {#if enabled}
        <div class="mature-option">
          {@render switchRow({ checked: blurOn, onchange: toggleBlur, title: 'Blur mature content',
            sub: 'Covered until you tap to reveal.', small: true })}
        </div>
      {/if}
    </div>
  {/if}
{/if}

<style>
  /* Full form: a field block that sits in the host's rhythm, no card of its own. */
  .mature {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .mature-row {
    display: flex;
    align-items: flex-start;
    gap: 0.625rem;
    margin: 0; /* the global label margin would leave uneven space under each row */
    cursor: pointer;
  }

  /* The native checkbox stays for keyboard and assistive tech; hidden off the
     flow, the global form styles (full width, padding, border) change nothing
     visible. */
  .mature-input {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
  }

  .mature-track {
    flex: none;
    position: relative;
    width: 38px;
    height: 22px;
    margin-top: 0.05rem;
    border-radius: 999px;
    background: var(--color-border);
    transition: background 0.2s ease;
  }

  .mature-thumb {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
    transition: transform 0.2s ease;
  }

  .mature-input:checked ~ .mature-track {
    background: var(--color-primary, #ff1b8d);
  }

  .mature-input:checked ~ .mature-track .mature-thumb {
    transform: translateX(16px);
  }

  .mature-input:focus-visible ~ .mature-track {
    outline: 2px solid var(--color-primary, #ff1b8d);
    outline-offset: 2px;
  }

  .mature-row.small .mature-track {
    width: 30px;
    height: 18px;
    margin-top: 0.1rem;
  }

  .mature-row.small .mature-thumb {
    width: 14px;
    height: 14px;
  }

  .mature-row.small .mature-input:checked ~ .mature-track .mature-thumb {
    transform: translateX(12px);
  }

  .mature-copy {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    min-width: 0;
  }

  .mature-title {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.875rem;
    font-weight: 600;
    line-height: 1.3;
    color: var(--color-text);
  }

  .mature-row.small .mature-title {
    font-size: 0.8rem;
  }

  /* Outline tag: the switch carries the state, so the tag never fills. */
  .mature-tag {
    padding: 0 0.3rem;
    border: 1px solid currentColor;
    border-radius: 4px;
    font-size: 0.65rem;
    font-weight: 700;
    line-height: 1.5;
    letter-spacing: 0.02em;
    color: var(--color-text-muted);
  }

  .mature.active .mature-tag,
  .mature-pill.active .mature-tag {
    color: var(--color-primary, #ff1b8d);
  }

  .mature-sub,
  .mature-note {
    font-size: 0.75rem;
    line-height: 1.4;
    color: var(--color-text-muted);
  }

  .mature-note {
    margin: 0;
  }

  /* The blur option indents under the main switch's copy column. */
  .mature-option {
    margin-left: calc(38px + 0.625rem);
  }

  .mature-confirm {
    padding: 0.625rem 0.75rem;
    border: 1px solid var(--color-border);
    border-radius: var(--radius);
    background: var(--color-surface);
  }

  .mature-confirm-text {
    margin: 0;
    font-size: 0.8rem;
    line-height: 1.45;
    color: var(--color-text);
  }

  .mature-confirm .mature-note {
    margin-top: 0.4rem;
  }

  .mature-confirm-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-top: 0.75rem;
  }

  .mature-btn {
    padding: 0.45rem 0.9rem;
    font-size: 0.8rem;
  }

  /* Compact pill for the wizard entry: a quiet chip beside the text link. */
  .mature-compact {
    position: relative;
    display: inline-flex;
  }

  .mature-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.25rem 0.85rem;
    border-radius: 999px;
    border: 1px solid var(--color-border);
    background: transparent;
    color: var(--color-text-muted);
    font-size: 0.8rem;
    font-weight: 500;
    cursor: pointer;
    transition: border-color 0.2s ease, color 0.2s ease;
  }

  .mature-pill:hover,
  .mature-pill:focus-visible {
    border-color: var(--color-text-light);
    color: var(--color-text);
  }

  .mature-pill:focus-visible {
    outline: 2px solid var(--color-primary, #ff1b8d);
    outline-offset: 2px;
  }

  .mature-pill.active {
    border-color: var(--color-primary-border);
    color: var(--color-text);
  }

  .mature-popover {
    position: absolute;
    top: calc(100% + 6px);
    left: 50%;
    transform: translateX(-50%);
    z-index: 6;
    width: min(320px, 86vw);
    text-align: left;
  }

  .mature-popover .mature-confirm {
    background: var(--color-bg);
    box-shadow: var(--shadow-lg);
  }

  /* On a phone the pill sits off centre, so a popover centred on it would run
     off the screen: show it as a sheet across the bottom instead. */
  @media (max-width: 480px) {
    .mature-popover {
      position: fixed;
      top: auto;
      bottom: max(1rem, env(safe-area-inset-bottom, 0px));
      left: 1rem;
      right: 1rem;
      width: auto;
      transform: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .mature-track,
    .mature-thumb,
    .mature-pill {
      transition: none;
    }
  }
</style>
